import { useState, useEffect } from "react";

const ENDPOINTS = [
  {
    category: "Signals",
    icon: "\u{1F4E1}",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/signals/active",
        desc: "Get currently open trade signals",
        auth: "API Key",
        response: `{
  "success": true,
  "count": 3,
  "signals": [
    {
      "signal_id": "vp_btc_4h_long_1741193700",
      "asset": "BTC",
      "direction": "LONG",
      "timeframe": "4H",
      "signal_type": "BREAKOUT_LONG",
      "entry_price": 71757.80,
      "stop_loss": 70322.64,
      "tp1": 72834.17,
      "tp2": 74269.32,
      "tp3": 75345.69,
      "risk_reward": 2.5,
      "ai_confidence": 82,
      "status": "TP1_HIT",
      "timestamp": "2026-03-04T10:11:00Z"
    }
  ]
}`,
      },
      {
        method: "GET",
        path: "/api/v1/signals/history",
        desc: "Past signals with PnL outcomes",
        auth: "API Key",
        params: [
          { name: "limit", type: "int", default: "50", desc: "Max results (1-200)" },
          { name: "asset", type: "string", default: "null", desc: "Filter by asset" },
          { name: "direction", type: "string", default: "null", desc: "Filter by LONG or SHORT" },
        ],
        response: `{
  "success": true,
  "count": 50,
  "signals": [
    {
      "signal_id": "vp_eth_4h_long_1741190820",
      "asset": "ETH",
      "direction": "LONG",
      "signal_type": "BULLISH_RECLAIM",
      "entry_price": 2079.60,
      "stop_loss": 2029.32,
      "tp1": 2131.59,
      "tp2": 2183.58,
      "tp3": 2245.97,
      "ai_confidence": 78,
      "status": "CLOSED",
      "pnl_percent": 3.42,
      "outcome": "WIN"
    }
  ]
}`,
      },
      {
        method: "WS",
        path: "/api/v1/signals/stream",
        desc: "Real-time WebSocket signal streaming",
        auth: "API Key (first message)",
        response: `// Connect
const ws = new WebSocket("wss://api.aipredict.app/api/v1/signals/stream")

// Authenticate within 10 seconds
ws.onopen = () => {
  ws.send(JSON.stringify({ api_key: "ignis_your_key_here" }))
}

// Receive signals
ws.onmessage = (event) => {
  const { type, data } = JSON.parse(event.data)
  // type: "new_signal" | "signal_update" | "active_signal" | "heartbeat"
  console.log(type, data)
}`,
      },
    ],
  },
  {
    category: "Webhooks",
    icon: "\u{1F517}",
    endpoints: [
      {
        method: "POST",
        path: "/api/v1/webhooks/register",
        desc: "Register webhook URL for push notifications",
        auth: "API Key",
        body: `{
  "url": "https://yourplatform.com/ignis-webhook",
  "events": ["signal.new", "signal.update", "signal.close"]
}`,
        response: `{
  "success": true,
  "webhook_id": "wh_a3f8c912",
  "url": "https://yourplatform.com/ignis-webhook"
}`,
      },
      { method: "GET", path: "/api/v1/webhooks", desc: "List your registered webhooks", auth: "API Key" },
      { method: "DELETE", path: "/api/v1/webhooks/{webhook_id}", desc: "Remove a webhook", auth: "API Key" },
    ],
  },
  {
    category: "Partners",
    icon: "\u{1F91D}",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/partner/info",
        desc: "Your account, subscriber count, and commission",
        auth: "API Key",
        response: `{
  "success": true,
  "partner_id": "partner_a3f8c912e4b7",
  "company_name": "TradeCorp",
  "subscriber_cap": 50,
  "active_subscribers": 23,
  "commission_rate": 15,
  "total_earned": 1725.00,
  "pending_payout": 375.00,
  "referral_code": "TRADECORP"
}`,
      },
      {
        method: "GET",
        path: "/api/v1/partner/subscribers",
        desc: "List subscribers from your platform",
        auth: "API Key",
        response: `{
  "success": true,
  "count": 23,
  "subscribers": [
    {
      "subscriber_id": "sub_x8f2",
      "tier": "bronze",
      "active": true,
      "subscribed_at": "2026-02-15T10:00:00Z",
      "your_commission": 75.00
    }
  ]
}`,
      },
      {
        method: "GET",
        path: "/api/v1/partner/earnings",
        desc: "Revenue share breakdown and payout history",
        auth: "API Key",
        params: [{ name: "months", type: "int", default: "3", desc: "Lookback period" }],
        response: `{
  "success": true,
  "commission_rate": 15,
  "monthly_breakdown": [
    { "month": "2026-03", "subscribers": 23, "revenue": 11500, "your_share": 1725 }
  ],
  "total_earned": 3075.00,
  "pending_payout": 375.00
}`,
      },
    ],
  },
  {
    category: "Performance",
    icon: "\u{1F4CA}",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/stats/overview",
        desc: "Win rate, PnL, drawdown, profit factor",
        auth: "API Key",
        params: [{ name: "days", type: "int", default: "30", desc: "Lookback period (1-365)" }],
        response: `{
  "success": true,
  "period_days": 30,
  "total_trades": 142,
  "wins": 89,
  "losses": 47,
  "win_rate": 62.7,
  "total_pnl_percent": 34.82,
  "avg_win_percent": 2.14,
  "avg_loss_percent": -1.03,
  "max_drawdown_percent": 4.21,
  "profit_factor": 1.87
}`,
      },
      { method: "GET", path: "/api/v1/stats/by-asset", desc: "Performance per asset", auth: "API Key" },
      { method: "GET", path: "/api/v1/stats/by-signal-type", desc: "Performance per signal type", auth: "API Key" },
    ],
  },
];

const PARTNER_STEPS = [
  { num: "01", title: "Apply to Partner", desc: "Tell us about your platform, user base, and integration plans. We review every application to ensure quality." },
  { num: "02", title: "Get API Access", desc: "Once approved, receive your API key, referral code, and agreed commission rate. We set a subscriber cap based on your platform." },
  { num: "03", title: "Integrate & Display", desc: "Show Ignis AI Agent signals on your platform via REST API, WebSocket, or webhooks. Your users see real-time signals and performance." },
  { num: "04", title: "Users Subscribe Through You", desc: "Your users go through standard Ignis onboarding \u2014 submit exchange API keys, pay via TRC-20 USDT. Your referral code tags every subscriber." },
  { num: "05", title: "Earn Commission", desc: "Earn a percentage of every subscriber\u2019s monthly payment. Track earnings and payouts through the Partner API." },
];

const REVENUE_TIERS = [
  { tier: "Bronze", price: 500, commission: "15%", earn: 75 },
  { tier: "Silver", price: 1000, commission: "15%", earn: 150 },
  { tier: "Gold", price: 2000, commission: "15%", earn: 300 },
];

const MC = { GET: { bg: "#0d9488", text: "#f0fdfa" }, POST: { bg: "#2563eb", text: "#eff6ff" }, DELETE: { bg: "#dc2626", text: "#fef2f2" }, WS: { bg: "#9333ea", text: "#faf5ff" } };

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: "relative", marginTop: 8 }}>
      <button onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 4, color: "#94a3b8", padding: "2px 8px", fontSize: 11, cursor: "pointer", zIndex: 2 }}>
        {copied ? "Copied!" : "Copy"}
      </button>
      <pre style={{ background: "#0c0f1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: 16, overflowX: "auto", fontSize: 12.5, lineHeight: 1.6, color: "#c9d1d9", margin: 0, fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>{code}</pre>
    </div>
  );
}

function EndpointCard({ ep }) {
  const [open, setOpen] = useState(false);
  const mc = MC[ep.method] || MC.GET;
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${open ? "rgba(234,179,8,0.3)" : "rgba(255,255,255,0.06)"}`, borderRadius: 10, marginBottom: 10, overflow: "hidden", transition: "border-color 0.2s" }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: "pointer", userSelect: "none" }}>
        <span style={{ background: mc.bg, color: mc.text, fontWeight: 700, fontSize: 11, padding: "3px 10px", borderRadius: 4, minWidth: 50, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", letterSpacing: 0.5 }}>{ep.method}</span>
        <code style={{ color: "#e2e8f0", fontSize: 13.5, fontFamily: "'JetBrains Mono', monospace", flex: 1 }}>{ep.path}</code>
        <span style={{ color: "#64748b", fontSize: 13 }}>{ep.desc}</span>
        <span style={{ color: "#64748b", fontSize: 18, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0)" }}>{"\u25BE"}</span>
      </div>
      {open && (
        <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ display: "flex", gap: 6, marginTop: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: "#94a3b8", background: "rgba(255,255,255,0.05)", padding: "2px 8px", borderRadius: 4 }}>Auth: {ep.auth}</span>
          </div>
          {ep.params && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6, fontWeight: 600 }}>Parameters</div>
              <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                <thead><tr style={{ color: "#64748b", textAlign: "left" }}><th style={{ padding: "4px 8px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Name</th><th style={{ padding: "4px 8px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Type</th><th style={{ padding: "4px 8px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Default</th><th style={{ padding: "4px 8px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Description</th></tr></thead>
                <tbody>{ep.params.map((p, i) => (<tr key={i} style={{ color: "#c9d1d9" }}><td style={{ padding: "4px 8px", fontFamily: "monospace" }}>{p.name}</td><td style={{ padding: "4px 8px", color: "#94a3b8" }}>{p.type}</td><td style={{ padding: "4px 8px", color: "#64748b" }}>{p.default}</td><td style={{ padding: "4px 8px", color: "#94a3b8" }}>{p.desc}</td></tr>))}</tbody>
              </table>
            </div>
          )}
          {ep.body && (<div style={{ marginBottom: 8 }}><div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>Request Body</div><CodeBlock code={ep.body} /></div>)}
          {ep.response && (<div><div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>{ep.method === "WS" ? "Usage Example" : "Response Example"}</div><CodeBlock code={ep.response} /></div>)}
        </div>
      )}
    </div>
  );
}

export default function IgnisAPIDocs() {
  const [activeTab, setActiveTab] = useState("overview");
  const [activeCat, setActiveCat] = useState("Signals");
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => { setTimeout(() => setHeroVisible(true), 100); }, []);

  const tabStyle = (t) => ({ padding: "8px 20px", cursor: "pointer", fontSize: 14, fontWeight: 600, background: "none", border: "none", color: activeTab === t ? "#eab308" : "#94a3b8", borderBottom: `2px solid ${activeTab === t ? "#eab308" : "transparent"}`, transition: "all 0.2s" });

  return (
    <div style={{ minHeight: "100vh", background: "#060812", color: "#e2e8f0", fontFamily: "'Outfit', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ position: "fixed", top: -200, right: -200, width: 600, height: 600, background: "radial-gradient(circle, rgba(234,179,8,0.06) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 40px", borderBottom: "1px solid rgba(255,255,255,0.06)", position: "sticky", top: 0, background: "rgba(6,8,18,0.9)", backdropFilter: "blur(20px)", zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: "#eab308", letterSpacing: -0.5 }}>IGNIS</span>
          <span style={{ fontSize: 14, color: "#64748b", fontWeight: 500 }}>AI Agent {"\u00B7"} Partner API</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <a href="/ignis" style={{ color: "#94a3b8", textDecoration: "none", fontSize: 13, padding: "6px 14px" }}>Ignis Home</a>
          <a href="mailto:partners@aipredict.app" style={{ background: "rgba(234,179,8,0.1)", color: "#eab308", fontSize: 12, padding: "6px 14px", borderRadius: 6, fontWeight: 600, border: "1px solid rgba(234,179,8,0.2)", textDecoration: "none" }}>Become a Partner</a>
        </div>
      </header>

      <div style={{ padding: "80px 40px 60px", textAlign: "center", position: "relative", zIndex: 1, opacity: heroVisible ? 1 : 0, transform: heroVisible ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s cubic-bezier(0.22, 1, 0.36, 1)" }}>
        <div style={{ display: "inline-block", background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.15)", borderRadius: 20, padding: "4px 16px", fontSize: 12, color: "#eab308", fontWeight: 600, marginBottom: 20 }}>AUTONOMOUS AI TRADING AGENT</div>
        <h1 style={{ fontSize: 48, fontWeight: 800, lineHeight: 1.1, margin: "0 0 16px", letterSpacing: -1.5, maxWidth: 750, marginLeft: "auto", marginRight: "auto" }}>
          <span style={{ color: "#f8fafc" }}>Partner With </span>
          <span style={{ background: "linear-gradient(135deg, #eab308, #f59e0b, #d97706)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Ignis AI Agent</span>
          <span style={{ color: "#f8fafc" }}> & Earn Revenue Share</span>
        </h1>
        <p style={{ fontSize: 17, color: "#94a3b8", maxWidth: 620, margin: "0 auto 32px", lineHeight: 1.6 }}>Integrate AI-powered crypto trading signals into your platform. Your users subscribe, you earn commission on every payment. We handle execution, risk management, and AI oversight.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button onClick={() => setActiveTab("partner")} style={{ background: "linear-gradient(135deg, #eab308, #d97706)", color: "#0c0a09", border: "none", padding: "12px 28px", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>How It Works</button>
          <button onClick={() => setActiveTab("docs")} style={{ background: "rgba(255,255,255,0.05)", color: "#e2e8f0", border: "1px solid rgba(255,255,255,0.1)", padding: "12px 28px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>API Reference</button>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto 48px", padding: "0 40px", position: "relative", zIndex: 1 }}>
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#eab308", marginBottom: 12, letterSpacing: 1 }}>QUICK START</div>
          <CodeBlock code={`# Get active Ignis AI Agent signals\ncurl -H "X-API-Key: ignis_your_key_here" \\\n  https://api.aipredict.app/api/v1/signals/active\n\n# View your partner stats + commission\ncurl -H "X-API-Key: ignis_your_key_here" \\\n  https://api.aipredict.app/api/v1/partner/info`} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 0, justifyContent: "center", marginBottom: 32, borderBottom: "1px solid rgba(255,255,255,0.06)", position: "relative", zIndex: 1 }}>
        {[{ key: "overview", label: "Overview" }, { key: "partner", label: "Partner Program" }, { key: "docs", label: "API Reference" }, { key: "auth", label: "Authentication" }].map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={tabStyle(t.key)}>{t.label}</button>
        ))}
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 40px 80px", position: "relative", zIndex: 1 }}>

        {activeTab === "overview" && (
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: "#f8fafc" }}>What Is Ignis?</h2>
            <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.8, marginBottom: 28 }}>Ignis is an autonomous AI trading agent built by AiPredict. It uses Volume Profile analysis, multi-timeframe confluence, order flow intelligence, and Claude AI oversight to generate and execute crypto trading signals across BTC, ETH, SOL, BNB, XRP, DOGE, LINK & HYPE.</p>
            <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.8, marginBottom: 28 }}>Every trade is reviewed by AI before execution. Ignis manages risk autonomously {"\u2014"} trailing stops, profit tiers, breakeven protection, daily profit locks, and session-aware filtering. Subscribers connect their exchange API keys, and Ignis trades on their behalf.</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 36 }}>
              {[{ label: "Assets Traded", value: "8", sub: "Major cryptos" }, { label: "Timeframes", value: "3", sub: "1H \u00B7 4H \u00B7 1D" }, { label: "AI Confidence", value: "65%+", sub: "Minimum threshold" }, { label: "Exchanges", value: "3", sub: "Bybit \u00B7 Binance \u00B7 HL" }].map((s, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 20, textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#eab308" }}>{s.value}</div>
                  <div style={{ fontSize: 13, color: "#f8fafc", fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{s.sub}</div>
                </div>
              ))}
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: "#f8fafc", marginBottom: 12 }}>Why Partner With Ignis?</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
              {[
                { icon: "\u{1F916}", title: "AI-Powered Edge", desc: "Every signal reviewed by Claude AI with multi-layer context \u2014 order flow, session dynamics, confluence scoring." },
                { icon: "\u{1F6E1}\uFE0F", title: "Risk Management Built-In", desc: "Trailing stops, profit tiers, daily profit locks, position limits, and breakeven protection." },
                { icon: "\u{1F4B0}", title: "Revenue Share", desc: "Earn a commission on every subscriber who joins through your platform. Recurring monthly income." },
                { icon: "\u{1F512}", title: "You Stay In Control", desc: "We cap subscribers per partner to ensure execution quality. Every account is monitored." },
              ].map((f, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 20 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>{f.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#f8fafc", marginBottom: 4 }}>{f.title}</div>
                  <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6 }}>{f.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 36 }}>
              <button onClick={() => setActiveTab("partner")} style={{ background: "linear-gradient(135deg, #eab308, #d97706)", color: "#0c0a09", border: "none", padding: "14px 36px", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>See Partner Program Details {"\u2192"}</button>
            </div>
          </div>
        )}

        {activeTab === "partner" && (
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: "#f8fafc" }}>Partner Program</h2>
            <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.7, marginBottom: 32 }}>Integrate Ignis AI Agent into your platform and earn recurring revenue. We handle AI, execution, and risk management. You bring the users.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 40 }}>
              {PARTNER_STEPS.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 20, padding: "20px 0", borderBottom: i < PARTNER_STEPS.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                  <div style={{ fontSize: 32, fontWeight: 800, color: "rgba(234,179,8,0.2)", fontFamily: "'JetBrains Mono', monospace", minWidth: 50 }}>{step.num}</div>
                  <div><div style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc", marginBottom: 4 }}>{step.title}</div><div style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.6 }}>{step.desc}</div></div>
                </div>
              ))}
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: "#f8fafc", marginBottom: 16 }}>Revenue Share Example</h3>
            <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>Commission rates are agreed during onboarding (typically 10-20%). All payments go through our TRC-20 USDT system. Example at 15%:</p>
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, overflow: "hidden", marginBottom: 28 }}>
              <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "rgba(234,179,8,0.05)" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", color: "#eab308", fontWeight: 700, fontSize: 12, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Ignis Tier</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", color: "#eab308", fontWeight: 700, fontSize: 12, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>User Pays</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", color: "#eab308", fontWeight: 700, fontSize: 12, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Commission</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", color: "#eab308", fontWeight: 700, fontSize: 12, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>You Earn/mo</th>
                </tr></thead>
                <tbody>{REVENUE_TIERS.map((t, i) => (
                  <tr key={i} style={{ borderBottom: i < REVENUE_TIERS.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                    <td style={{ padding: "12px 16px", color: "#f8fafc", fontWeight: 600 }}>{t.tier}</td>
                    <td style={{ padding: "12px 16px", color: "#e2e8f0" }}>${t.price} USDT</td>
                    <td style={{ padding: "12px 16px", color: "#94a3b8" }}>{t.commission}</td>
                    <td style={{ padding: "12px 16px", color: "#eab308", fontWeight: 700 }}>${t.earn} USDT</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div style={{ background: "rgba(234,179,8,0.04)", border: "1px solid rgba(234,179,8,0.12)", borderRadius: 10, padding: 20, marginBottom: 28 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#eab308", marginBottom: 8 }}>Example: 30 subscribers via your platform</div>
              <div style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.7 }}>30 users at Bronze ($500/mo) with 15% commission = <span style={{ color: "#eab308", fontWeight: 700 }}>$2,250/month</span> in recurring revenue.</div>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: "#f8fafc", marginBottom: 12 }}>What We Control</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 32 }}>
              {[
                { label: "Subscriber Cap", desc: "Max subscribers per partner to protect execution quality" },
                { label: "Payment Flow", desc: "All payments through our TRC-20 USDT system" },
                { label: "Risk Management", desc: "We manage all trading risk \u2014 partners never touch user funds" },
                { label: "Onboarding Standards", desc: "Every subscriber goes through our verification process" },
              ].map((c, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#f8fafc", marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>{c.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center" }}>
              <a href="mailto:partners@aipredict.app" style={{ display: "inline-block", background: "linear-gradient(135deg, #eab308, #d97706)", color: "#0c0a09", padding: "14px 36px", borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: "none" }}>Apply to Partner Program {"\u2192"}</a>
            </div>
          </div>
        )}

        {activeTab === "docs" && (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
              {ENDPOINTS.map((cat) => (
                <button key={cat.category} onClick={() => setActiveCat(cat.category)} style={{ background: activeCat === cat.category ? "rgba(234,179,8,0.12)" : "rgba(255,255,255,0.03)", color: activeCat === cat.category ? "#eab308" : "#94a3b8", border: `1px solid ${activeCat === cat.category ? "rgba(234,179,8,0.25)" : "rgba(255,255,255,0.06)"}`, padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>{cat.icon} {cat.category}</button>
              ))}
            </div>
            {ENDPOINTS.filter((c) => c.category === activeCat).map((cat) => (
              <div key={cat.category}>{cat.endpoints.map((ep, i) => (<EndpointCard key={i} ep={ep} />))}</div>
            ))}
          </div>
        )}

        {activeTab === "auth" && (
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: "#f8fafc" }}>Authentication</h2>
            <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.7, marginBottom: 28 }}>All API requests require your partner API key via the <code style={{ color: "#eab308", background: "rgba(234,179,8,0.08)", padding: "1px 6px", borderRadius: 4, fontSize: 13 }}>X-API-Key</code> header.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>REST API</div>
                <CodeBlock code={`curl -H "X-API-Key: ignis_your_key_here" \\\n  https://api.aipredict.app/api/v1/signals/active`} />
              </div>
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>WebSocket</div>
                <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 12 }}>Connect first, then authenticate within 10 seconds:</p>
                <CodeBlock code={`const ws = new WebSocket("wss://api.aipredict.app/api/v1/signals/stream")\n\nws.onopen = () => {\n  ws.send(JSON.stringify({ api_key: "ignis_your_key_here" }))\n}\n\nws.onmessage = (event) => {\n  const { type, data } = JSON.parse(event.data)\n  if (type === "new_signal") {\n    console.log("New signal:", data.asset, data.direction)\n  }\n}`} />
              </div>
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>Webhook Verification</div>
                <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 12 }}>Verify the HMAC-SHA256 signature on incoming webhooks:</p>
                <CodeBlock code={`import hmac, hashlib\n\ndef verify_webhook(payload, signature, secret):\n    expected = hmac.new(\n        secret.encode(),\n        payload.encode(),\n        hashlib.sha256\n    ).hexdigest()\n    return hmac.compare_digest(expected, signature)`} />
              </div>
            </div>
          </div>
        )}
      </div>

      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "28px 40px", textAlign: "center", color: "#475569", fontSize: 13 }}>
        <span style={{ color: "#eab308", fontWeight: 700 }}>IGNIS</span> AI Agent by AiPredict {"\u2014"} Autonomous Crypto Trading Intelligence<br />
        <span style={{ fontSize: 12, color: "#334155" }}>{"\u00A9"} 2026 AiPredict. All rights reserved. {"\u00B7"} partners@aipredict.app</span>
      </footer>
    </div>
  );
}